import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useState } from 'react';

const ENDPOINT_URL = '/shp-shipping/pickup-spot/{id}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetPickupSpotDetailInput = paths[url]['get']['parameters']['path'];
export type GetPickupSpotDetailSuccessResponse = paths[url]['get']['responses']['200']['content']['application/json'];

/**
 * Requests endpoint to get nearby pickup spots.
 */
export const useGetPickupSpotDetail = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setPickupSpotDetail } = useData();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async (props: GetPickupSpotDetailInput) => {
        setLoading(true);
        setError(null);

        try {
            const inputParameters = new URLSearchParams({ ...getCommonParameters()}).toString();

            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{id}', props.id || '')}?${inputParameters}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                let error;

                try {
                    const json = await response.json();
                    error = new Error(json.message || '') as ApiError;
                    error.statusCode = response.status;
                } catch (error: any) {
                    error = new Error() as ApiError;
                    error.statusCode = response.status;
                }
                
                throw error;
            }

            const data: GetPickupSpotDetailSuccessResponse = await response.json();

            setPickupSpotDetail(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setError(error.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, lastResponseTime };
};
